import { useEffect, useState } from "react";
import { Button, Form, Header, Icon, Message, Segment, Table } from "semantic-ui-react";

const BOOKING_TYPE_LABELS = { OFFICIAL: "Official", NON_OFFICIAL: "Non-official" };
const ACTOR_ROLE_LABELS = { ADMIN: "Admin", EXTERNAL: "Namaste Applicant" };

/* One row per (booking_type, actor_role) -- the 4 rows are seeded server-side
   and never added to or removed from here, only max_days is ever edited. An
   empty value means no limit (NULL), same convention as GuestHouse's own
   auto_book_max_stay_nights "No limit" field. */
function LimitRow({ limit, onSave }) {
  const [value, setValue] = useState(limit.max_days ?? "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    setValue(limit.max_days ?? "");
    setSaved(false);
  }, [limit.max_days]);

  const dirty = (value === "" ? null : Number(value)) !== (limit.max_days ?? null);

  const handleSave = async () => {
    setSaving(true);
    setError("");
    try {
      await onSave(limit.id, value === "" ? null : Number(value));
      setSaved(true);
    } catch (e) {
      setError(e.message || "Failed to save this limit.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Table.Row>
      <Table.Cell>{BOOKING_TYPE_LABELS[limit.booking_type] || limit.booking_type}</Table.Cell>
      <Table.Cell>{ACTOR_ROLE_LABELS[limit.actor_role] || limit.actor_role}</Table.Cell>
      <Table.Cell width={4}>
        <Form.Input
          type="number"
          min={0}
          placeholder="No limit"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setSaved(false);
          }}
        />
      </Table.Cell>
      <Table.Cell width={4}>
        <Button primary size="small" disabled={!dirty || saving} loading={saving} onClick={handleSave}>
          Save
        </Button>
        {saved && !dirty && <Icon name="check circle" color="green" style={{ marginLeft: 8 }} />}
        {error && <Message negative size="tiny" content={error} style={{ marginTop: 8, marginBottom: 0 }} />}
      </Table.Cell>
    </Table.Row>
  );
}

export default function AdvanceBookingLimitsTab({ limits, onUpdate }) {
  const sorted = [...(limits || [])].sort((a, b) => {
    if (a.booking_type !== b.booking_type) return a.booking_type.localeCompare(b.booking_type);
    return a.actor_role.localeCompare(b.actor_role);
  });

  return (
    <Segment>
      <Header as="h4">Advance Booking Limits</Header>
      <p style={{ fontSize: 12, color: "rgba(0,0,0,0.6)" }}>
        How far ahead of today an arrival date may be requested, by booking type and by who
        is raising the indent — applies across every guest house. Leave blank for no limit.
      </p>
      {sorted.length === 0 ? (
        <Message content="No advance booking limits found." />
      ) : (
        <Table basic="very">
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Booking Type</Table.HeaderCell>
              <Table.HeaderCell>Raised By</Table.HeaderCell>
              <Table.HeaderCell>Max Days Ahead</Table.HeaderCell>
              <Table.HeaderCell />
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {sorted.map((limit) => (
              <LimitRow key={limit.id} limit={limit} onSave={onUpdate} />
            ))}
          </Table.Body>
        </Table>
      )}
    </Segment>
  );
}
